import { getSupabaseClient } from '@/hooks/useOrdersManagement/supabaseClients';

export interface ImageUploadResult {
  url: string;
  path: string;
  success: boolean;
  error?: string;
}

export const uploadProductImage = async (
  file: File,
  productId?: string
): Promise<ImageUploadResult> => {
  try {
    // Basic validation (keep existing UX)
    if (!file.type.startsWith('image/')) {
      throw new Error('Only image files are allowed');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image size must be less than 5MB');
    }

    // Use SMS-authenticated Supabase client to avoid RLS issues
    const supabaseClient = getSupabaseClient();
    const { uploadProductImage: coreUpload } = await import('@/lib/uploadProductImage');
    const pid = productId || 'temp';
    const res = await coreUpload({ supabase: supabaseClient, productId: pid, file });

    return {
      url: res.publicUrl || '',
      path: res.path,
      success: true,
    };
  } catch (error: any) {
    return {
      url: '',
      path: '',
      success: false,
      error: error?.message || 'Upload failed',
    };
  }
};


export const deleteProductImage = async (imagePath: string): Promise<boolean> => {
  try {
    const supabaseClient = getSupabaseClient();
    const { error } = await supabaseClient.storage
      .from('product-images')
      .remove([imagePath]);

    if (error) {
      console.error('Failed to delete image:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete image error:', error);
    return false;
  }
};

// Convert a data URL to a File object
export const dataUrlToFile = async (dataUrl: string, fileName: string) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
};

// Normalize various photo inputs into persistent public URLs
// - Accepts string | string[] (including JSON-string arrays)
// - Uploads any data: URLs to Supabase Storage
// - Skips unrecoverable blob: URLs
export const normalizePhotoInputs = async (
  photos: unknown,
  productId?: string
): Promise<string[]> => {
  let arr: string[] = [];
  if (!photos) return [];

  if (Array.isArray(photos)) {
    arr = photos.filter((p): p is string => typeof p === 'string');
  } else if (typeof photos === 'string') {
    const str = photos.trim();
    if (str.startsWith('[')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) arr = parsed.filter((p: any) => typeof p === 'string');
      } catch {
        arr = [str];
      }
    } else if (str.includes(',')) {
      arr = str.split(',').map(s => s.trim()).filter(Boolean);
    } else {
      arr = [str];
    }
  } else {
    return [];
  }

  const urls: string[] = [];
  for (const u of arr) {
    if (!u || typeof u !== 'string') continue;
    if (u.startsWith('data:')) {
      try {
        const file = await dataUrlToFile(u, `photo-${Date.now()}.jpg`);
        const result = await uploadProductImage(file, productId);
        if (result.success) urls.push(result.url);
      } catch (e) {
        console.warn('Failed to normalize data URL photo', e);
      }
    } else if (u.startsWith('blob:')) {
      // Unrecoverable after reload; require re-upload
      continue;
    } else {
      urls.push(u);
    }
  }
  return urls;
};
